import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const CollaborationPanel = ({ collaborators, planId, patientConsent }) => {
  const [comments, setComments] = useState([
    {
      id: 1,
      author: 'Dr. Michael Chen',
      role: 'Primary Dentist',
      timestamp: '2026-01-18 10:30 AM',
      content: 'Treatment plan approved. Patient is a good candidate for implant placement. Recommend CBCT scan before proceeding with phase 3.',
      type: 'approval'
    },
    {
      id: 2,
      author: 'Sarah Williams, RDH',
      role: 'Dental Hygienist',
      timestamp: '2026-01-17 02:15 PM',
      content: 'Patient education completed. Discussed home care instructions and importance of regular maintenance visits.',
      type: 'note'
    },
    {
      id: 3,
      author: 'Dr. Emily Rodriguez',
      role: 'Oral Surgeon',
      timestamp: '2026-01-16 11:45 AM',
      content: 'Reviewed imaging. Bone density is adequate for implant placement. Will need minor bone graft for optimal results. Awaiting patient consent.',
      type: 'recommendation'
    }
  ]);

  const [newComment, setNewComment] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-success/10 text-success border-success';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning';
      case 'rejected':
        return 'bg-error/10 text-error border-error';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const handleAddComment = () => {
    if (!newComment?.trim()) return;
    
    const comment = {
      id: comments?.length + 1,
      author: 'Current User',
      role: 'Dentist',
      timestamp: new Date()?.toLocaleString(),
      content: newComment,
      type: 'note'
    };
    
    setComments([comment, ...comments]);
    setNewComment('');
  };

  return (
    <div className="space-y-6">
      {/* Collaborators & Approvals */}
      <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Users" size={20} className="text-primary" />
          Multi-Disciplinary Team
        </h3>

        <div className="space-y-3">
          {collaborators?.map((collaborator, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary">
                    {collaborator?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-foreground">{collaborator?.name}</p>
                  <p className="text-sm text-muted-foreground">{collaborator?.role}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {collaborator?.date && (
                  <span className="text-sm text-muted-foreground">{collaborator?.date}</span>
                )}
                <span className={cn(
                  "inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border",
                  getStatusColor(collaborator?.status)
                )}>
                  {collaborator?.status?.toUpperCase()}
                </span>
              </div>
            </div>
          ))}
        </div>

        <Button variant="outline" size="sm" className="w-full mt-4" iconName="UserPlus">
          Add Collaborator
        </Button>
      </div>

      {/* Comments & Communication */}
      <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="MessageSquare" size={20} className="text-primary" />
          Team Communication
        </h3>

        {/* Add Comment */}
        <div className="mb-4">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e?.target?.value)}
            placeholder="Add a comment or note..."
            className="w-full min-h-[80px] p-3 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
          <div className="flex items-center gap-2 mt-2">
            <Button variant="default" size="sm" iconName="Send" onClick={handleAddComment}>
              Post Comment
            </Button>
            <Button variant="ghost" size="sm" iconName="Paperclip">
              Attach
            </Button>
          </div>
        </div>

        {/* Comments List */}
        <div className="space-y-3">
          {comments?.map((comment) => (
            <div key={comment?.id} className="p-3 bg-background rounded-lg border border-border">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-semibold text-primary">
                      {comment?.author?.split(' ')?.map(n => n?.[0])?.join('')}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{comment?.author}</p>
                    <p className="text-xs text-muted-foreground">{comment?.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded ${
                    comment?.type === 'approval' ? 'bg-success/10 text-success' :
                    comment?.type === 'recommendation'? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {comment?.type}
                  </span>
                  <span className="text-xs text-muted-foreground">{comment?.timestamp}</span>
                </div>
              </div>
              <p className="text-sm text-foreground">{comment?.content}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Approval Workflow */}
      <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="CheckCircle" size={20} className="text-primary" />
          Approval Workflow
        </h3>

        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-success/10 rounded-lg">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Primary Dentist Approval</p>
              <p className="text-xs text-muted-foreground">Approved by Dr. Michael Chen on 2026-01-10</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-warning/10 rounded-lg">
            <Icon name="Clock" size={20} className="text-warning" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Specialist Review Pending</p>
              <p className="text-xs text-muted-foreground">Awaiting approval from Dr. Emily Rodriguez</p>
            </div>
            <Button variant="outline" size="xs" iconName="Send">
              Send Reminder
            </Button>
          </div>

          <div className="flex items-center gap-3 p-3 bg-success/10 rounded-lg">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Hygienist Approval</p>
              <p className="text-xs text-muted-foreground">Approved by Sarah Williams, RDH on 2026-01-12</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollaborationPanel;